/**
 * Razorpay Checkout, wrapped in a promise.
 *
 * Their SDK is a global loaded from a script tag and driven by callbacks. This
 * turns it into one `await` that either resolves with the three fields the
 * server needs to verify, or rejects — so the calling component reads as a
 * normal async flow rather than a callback maze.
 *
 * THE KEY ID IS PUBLIC. `VITE_RAZORPAY_KEY_ID` ships to the browser and is
 * meant to; it identifies the merchant. The key SECRET never leaves the
 * server, which is what makes the signature worth checking. If a key secret
 * ever appears with a VITE_ prefix, env.ts refuses to boot.
 *
 * NOTHING HERE IS TRUSTED. A success callback is a claim made by a browser, so
 * the server re-derives the signature from its own secret before crediting
 * anything. This file is UX, not security.
 */

const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

export const RAZORPAY_KEY_ID: string = import.meta.env.VITE_RAZORPAY_KEY_ID ?? '';

export interface CheckoutResult {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

/** Thrown when the user closes the sheet. Not an error worth showing in red. */
export class CheckoutDismissed extends Error {
  constructor() {
    super('Payment cancelled.');
    this.name = 'CheckoutDismissed';
  }
}

interface RazorpayHandlerResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpayInstance {
  open: () => void;
  on: (event: string, handler: (payload: unknown) => void) => void;
}

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => RazorpayInstance;
  }
}

let loader: Promise<void> | null = null;

/**
 * Load their script once, no matter how many times checkout is opened.
 *
 * The promise is cached rather than the boolean, so two dialogs opened in the
 * same tick both wait on the same load instead of injecting two script tags.
 */
function loadCheckout(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  if (loader) return loader;

  loader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let the next attempt retry rather than caching the failure forever —
      // this is usually a dropped connection, not a permanent state.
      loader = null;
      reject(new Error('Could not load the payment window. Check your connection and try again.'));
    };
    document.head.appendChild(script);
  });

  return loader;
}

export interface CheckoutOptions {
  gatewayOrderId: string;
  amountPaise: number;
  description: string;
  /** Prefilled on the sheet so the user does not retype what we already know. */
  name?: string | null;
  email?: string | null;
  contact?: string | null;
}

/**
 * Open the sheet and wait for it to finish.
 *
 * Resolves only on a successful payment. A dismissal rejects with
 * `CheckoutDismissed`, which the caller should treat as "nothing happened"
 * rather than as a failure — the user simply changed their mind.
 *
 * `payment.failed` is deliberately NOT rejected on: Razorpay keeps the sheet
 * open so the user can try another card, and rejecting here would tear down
 * the dialog underneath them mid-retry. The dismissal path covers giving up.
 */
export async function openCheckout(options: CheckoutOptions): Promise<CheckoutResult> {
  await loadCheckout();

  const Checkout = window.Razorpay;
  if (!Checkout) throw new Error('The payment window did not load. Try again.');

  if (!RAZORPAY_KEY_ID) {
    throw new Error('Payments are not configured. VITE_RAZORPAY_KEY_ID is not set.');
  }

  return new Promise<CheckoutResult>((resolve, reject) => {
    let settled = false;

    const rzp = new Checkout({
      key: RAZORPAY_KEY_ID,
      order_id: options.gatewayOrderId,
      amount: options.amountPaise,
      currency: 'INR',
      name: 'MakeMyPartner',
      description: options.description,
      prefill: {
        ...(options.name ? { name: options.name } : {}),
        ...(options.email ? { email: options.email } : {}),
        ...(options.contact ? { contact: options.contact } : {}),
      },
      theme: { color: '#E8A33D' },
      handler: (response: RazorpayHandlerResponse) => {
        settled = true;
        resolve({
          razorpayOrderId: response.razorpay_order_id,
          razorpayPaymentId: response.razorpay_payment_id,
          razorpaySignature: response.razorpay_signature,
        });
      },
      modal: {
        ondismiss: () => {
          // `handler` fires before `ondismiss` on success, so the flag stops a
          // completed payment being reported as a cancellation.
          if (!settled) reject(new CheckoutDismissed());
        },
      },
    });

    rzp.on('payment.failed', (payload: unknown) => {
      const p = payload as { error?: { description?: string; reason?: string } } | undefined;
      // Logged, not thrown — the sheet stays open for another attempt.
      console.warn('razorpay payment failed', p?.error?.description ?? p?.error?.reason);
    });

    rzp.open();
  });
}
